/**
CREATIVE CODING
---
Kill server : CTRL + C
Start server : npm run start
Final build : npm run build
---
**/
import AxL from "./axl";
import { DATA_STORE } from "./DataStore";
import Bookmark from "./layout/Bookmark";
import Layout from "./layout/Layout";
import Toggle from "./Toggle";
import { loadJson, PRNG, scrollTo, scroll } from "./utils";
import Tracking from "./Tracking";
import Tooltips from "./Tooltips.js";

const USE_WORKER = false;

export default class App {
  constructor() {
    console.log(
      "%c ✨ CANVAS EXPERIMENT ✨ ",
      "background: #000; color: #f0f; padding:10px;"
    );

    // randomize the PRNG
    PRNG.setSeed(Date.now());

    this.firstResume = true;
    this.prependApp();
  }

  iOS() {
    return (
      [
        "iPad Simulator",
        "iPhone Simulator",
        "iPod Simulator",
        "iPad",
        "iPhone",
        "iPod",
      ].includes(navigator.platform) ||
      // iPad on iOS 13 detection
      (navigator.userAgent.includes("Mac") && "ontouchend" in document)
    );
  }

  async prependApp() {
    ////AXL
    this.axl = new AxL();
    if (window._y010) {
      await this.preloadLayout();
      this.initApp();
    } else {
      this.initAxL();
    }
  }

  async initAxL() {
    //--------- PRELOAD OPTIONS HERE
    //const preloadData = await preloader();

    //------------
    const gotFirstResume = new Promise((resolve) => {
      this.axl.registerMessageCallback(AxL.HostToChild.RESUME, (payload) => {
        if (this.firstResume) {
          this.firstResume = false;
          console.log("init RESUME ");
          resolve();
        } else {
          console.warn("RESUME , and should scroll to ", scroll.y);
          if (scroll.y)
            setTimeout(() => {
              window.scrollTo(0, scroll.y);
              scroll.y = null;
            }, 10);
        }
      });
    });

    const preloadThings = new Promise(async (resolve) => {
      resolve();
    });

    const gameReady = this.axl.handshake().then(async (initialData) => {
      try {
        this.axl.registerMessageCallback(AxL.HostToChild.FREEZE, () => {
          // Pause videos, mute audio, stop super fancy GPU shaders;
          console.warn("FREEZE , and should store scroll", scroll.y);
        });

        console.warn("handshake is done", initialData);
        let objJsonStr = JSON.stringify(initialData);
        let objJsonB64 = Buffer.from(objJsonStr).toString("base64");
        console.warn(objJsonB64);
        this.tracker = new Tracking(this.axl);
        await this.preloadLayout();
        this.INITIAL_BOOKMARK = initialData.bookmarks;
        if (this.INITIAL_BOOKMARK && this.INITIAL_BOOKMARK.length > 0) {
          this.cleanupPage();
        }
      } catch (error) {
        console.log("ERROR", error);
      }
    });

    // Make sure game is initialized AND we got the first resume call
    Promise.all([gotFirstResume, preloadThings, gameReady]).then(() => {
      console.log("init app");
      this.initApp();
    });
  }

  async initApp() {
    const queryString = window.location.search;
    this.urlParams = new URLSearchParams(queryString);

    // loader
    const loader = document.querySelector(".loader");
    const progress = document.querySelector(".progress");

    // toggle label
    this.label = document
      .querySelector(".card")
      .getElementsByTagName("label")[0];

    //get h1
    this.letter = document.querySelector(".letter");
    const header = this.letter.querySelector("h1");
    header.addEventListener("click", async () => {
      // prevent header click in edit mode
      if (document.querySelector(".doubleButton").classList.contains("checked"))
        return;

      // display intro + random contents
      await this.layout.introduction(true);
      await this.layout.categories(3, 7, false);

      scrollTo(0, this.iOS());

      if (this.axl) {
        this.tracker.track("HOME_CLICKED");
      }
    });

    const showPreload = () => {
      // unblur show contents when everything is loaded
      this.layout.once("all-contents-loaded", () => {
        loader.classList.add("hidden");
        document.body.classList.remove("loading");
      });
      // scroll up to prevent infinite scroll to trigger
      scrollTo(0, this.iOS());

      // wait for the content to be loaded
      document.body.classList.add("loading");
      loader.classList.remove("hidden");
      progress.innerHTML = "";
    };

    // used when the search result is a tag or a when a tag was cliked
    const preloadTags = async (tag, clear = false) => {
      this.forceSwitchClassicView();
      showPreload();
      await this.layout.tags(tag, clear);
    };

    //listen to the tags clicks
    this.layout.on("tag-clicked", async (tag) => {
      // preloads a specific tag
      preloadTags(tag, true);
      console.table("LOADING TAG:", tag, DATA_STORE.tags[tag].length);
      if (this.axl) {
        this.tracker.track("TAG_CLICKED", tag);
      }
    });

    const preloadResultPage = async (tag, assets, type) => {
      // scroll to top
      scrollTo(0, this.iOS());

      if (type == "tag") {
        // clears the page and display the tag
        preloadTags(tag, true);
        if (this.axl) {
          this.tracker.track("SEARCH_TAG", tag);
        }
      } else if (type == "search") {
        console.table("search");
        // clears the page and display the search result
        if (assets.length == 0) {
          // no results
          await this.layout.assets(
            assets.length + " search results for: " + tag,
            assets,
            true
          );
        } else {
          // blur the page while loading
          preloadSearchResults(tag, assets, true);
        }
        if (this.axl) {
          this.tracker.track("SEARCH", tag);
        }
      }
    };

    // search result
    this.layout.on("search-result", preloadResultPage);

    // used when the search result is a a list of assets
    const preloadSearchResults = async (
      searchTerm,
      assetslist,
      clear = true
    ) => {
      showPreload();
      await this.layout.assets(
        assetslist.length + " search results for: " + searchTerm,
        assetslist,
        clear
      );
    };

    // infiniteScroll
    this.layout.on("touch-bottom", async () => {
      // we're viewing the custom edition and don't want anything to be added to the page
      if (Bookmark.EDIT == true) {
        return;
      }

      // we're preloading something
      if (this.layout.loading == true) {
        return;
      }

      // a bit brutal maybe
      // this.layout.resetTagListeners();

      if (this.layout.notReady) {
        this.layout.notReady = false;
        await this.layout.categories(3, 7, false);
      }
    });

    const toggle = new Toggle(document.querySelector(".doubleButton"));
    toggle.on("toggle", (value) => {
      // scroll to top
      scrollTo(0, this.iOS());

      if (value) {
        // displays the user selection or an intructions panel
        document.body.classList.add("modern");
        Bookmark.EDIT = true;
        Bookmark.INTERACTION = true;
        Bookmark.HIDE_SHARING = false;
        this.layout.saveState();
        this.layout.clear();
        this.layout.bookmarks();
        this.previousTag = this.urlParams.get("tag");

        // window.history.pushState(
        //   { tag: "bookmarks" },
        //   "bookmarks",
        //   `?tag=bookmarks`
        // );
      } else {
        document.querySelector("#bookmarks").textContent = "My Bookmarks";

        // display the regular page (tags, random, categories, search results)
        document.body.classList.remove("modern");
        Bookmark.EDIT = false;
        Bookmark.INTERACTION = true;
        this.layout.restoreState();
        // window.history.pushState(
        //   { tag: this.previousTag },
        //   `${this.previousTag}`,
        //   `?tag=${this.previousTag}`
        // );
      }

      if (this.axl && this.tracker) {
        this.tracker.track("UI_TOGGLE", value);
      }
    });

    window.addEventListener("resize", this.resize.bind(this));

    // window.addEventListener("scroll", this.scroll.bind(this));

    // window.addEventListener("popstate", async (event) => {
    //   // Can access state data using event.state.data
    //   if (event.state) {
    //     switch (event.state.tag) {
    //       case "home":
    //         document.body.classList.remove("modern");
    //         toggle.el.checked = false;
    //         PRNG.setSeed(1);
    //         await this.layout.introduction(true);
    //         await this.layout.categories(3, 7, false);

    //         break;

    //       case "bookmarks":
    //         // toggle.onClick();
    //         document.body.classList.add("modern");
    //         toggle.el.checked = true;
    //         // Bookmark.EDIT = true;
    //         // this.layout.saveState();
    //         this.layout.bookmarks();
    //         this.previousTag = this.urlParams.get("tag");

    //         break;
    //       default:
    //         document.body.classList.remove("modern");
    //         toggle.el.checked = false;
    //         console.log("default config by tag", event.state.tag);
    //         preloadTags(event.state.tag, true);
    //     }
    //   }
    // });

    // document.addEventListener("click", (e) => {
    //   document.body.classList.toggle("modern");
    // });

    // const title = document.querySelector(".letter").getElementsByTagName("h1")[0];
    // title.addEventListener("click", () => {

    // });
    // console.log(title);

    // if  this.INITIAL_BOOKMARKS is not empty, we need to load the bookmarks layout
    if (this.INITIAL_BOOKMARK && this.INITIAL_BOOKMARK.length > 0) {
      Bookmark.EDIT = true;
      Bookmark.INTERACTION = false;
      Bookmark.selection = [];
    }
  }

  cleanupPage() {
    if (this.INITIAL_BOOKMARK && this.INITIAL_BOOKMARK.length > 0) {
      Bookmark.selection = this.INITIAL_BOOKMARK;
      this.layout.saveState();
      this.layout.bookmarks();
      document.body.classList.add("modern");
      document.querySelector("#bookmarks").textContent = "Special Edition";
      document.querySelector(".doubleButton").classList.add("checked");
      Array.from(document.querySelector(".doubleButton").children).forEach(
        (item) => {
          item.classList.toggle("active");
        }
      );
    }
  }

  preloadLayout() {
    return new Promise(async (resolve) => {
      await this.preloadData();
      this.toolTips = new Tooltips();
      this.layout = new Layout(
        USE_WORKER,
        this.axl,
        this.tracker,
        this.toolTips
      );
      await this.layout.introduction();
      await this.layout.categories(3, 7, false);
      this.layout.once("all-contents-loaded", () => {
        scrollTo(0, this.iOS());
        resolve();
      });
    });
  }

  // preloads the JOSN contents
  async preloadData() {
    // fetch verso-recto dictionnary
    this.versoRecto = await loadJson("./json/verso-recto.json");
    DATA_STORE.versoRecto = this.versoRecto;

    //fetch the motherload
    const motherload = await loadJson("./json/payload.json");
    DATA_STORE.parse(motherload);

    console.table("DATASTORE ready");
  }

  resize() {
    this.layout.resize();
  }

  forceSwitchClassicView() {
    if (document.body.classList.contains("modern")) {
      Bookmark.EDIT = false;
      Bookmark.INTERACTION = true;
      document.body.classList.remove("modern");
      document.querySelector(".doubleButton").classList.remove("checked");
      Array.from(document.querySelector(".doubleButton").children).forEach(
        (item) => {
          item.classList.toggle("active");
        }
      );
    }
  }
}
